/**
 * CyberSells Player Data Module
 * Manages player progression data in localStorage with private mode fallback
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'cybersells_player_data';
  const VERSION = 1;
  
  // In-memory fallback for private browsing mode
  let _inMemoryStorage = null;
  let _isPrivateMode = false;
  
  /**
   * Detect if localStorage is available (not in private mode)
   */
  function checkStorageAvailable() {
    try {
      const testKey = '__cybersells_storage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      return false;
    }
  }
  
  _isPrivateMode = !checkStorageAvailable();

  /**
   * Default player data structure
   */
  function getDefaultPlayerData() {
    return {
      version: VERSION,
      playerId: generatePlayerId(),
      createdAt: Date.now(),
      lastSeen: Date.now(),
      
      // Core stats
      stats: {
        str: 0,  // Strength
        agi: 0,  // Agility
        int: 0,  // Intelligence
        vit: 0,  // Vitality
        luk: 0,  // Luck
        damage: 0,
        defense: 0,
        speed: 0,
        charisma: 0,
        stealth: 0,
        perception: 0,
        endurance: 0
      },
      
      // Progression
      level: 1,
      xp: 0,
      totalXpEarned: 0,
      
      // Purchases
      itemsPurchased: [],
      totalSpent: 0,
      purchaseCount: 0,
      
      // Achievements
      achievements: [],
      achievementCount: 0,
      
      // Activity tracking
      pageViews: 0,
      productsViewed: [],
      cartAdds: 0,
      
      // Session data
      currentSession: {
        startTime: Date.now(),
        pageViews: 0,
        productsViewed: []
      }
    };
  }

  /**
   * Generate a unique player ID
   */
  function generatePlayerId() {
    return 'player_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Load player data from localStorage or in-memory fallback
   */
  function loadPlayerData() {
    try {
      // Use in-memory storage in private mode
      if (_isPrivateMode) {
        if (!_inMemoryStorage) {
          _inMemoryStorage = getDefaultPlayerData();
        }
        return _inMemoryStorage;
      }
      
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return getDefaultPlayerData();
      }

      const data = JSON.parse(stored);
      
      // Version migration if needed
      if (data.version !== VERSION) {
        return migratePlayerData(data);
      }
      
      // Update last seen
      data.lastSeen = Date.now();
      
      return data;
    } catch (error) {
      // Silent fallback - don't log in production
      if (window.cyberSellsConfig?.debug) {
        console.error('[CyberSells] Error loading player data:', error);
      }
      return getDefaultPlayerData();
    }
  }

  /**
   * Save player data to localStorage or in-memory fallback
   */
  function savePlayerData(data) {
    try {
      data.lastSeen = Date.now();
      
      // Use in-memory storage in private mode
      if (_isPrivateMode) {
        _inMemoryStorage = data;
        return true;
      }
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      return true;
    } catch (error) {
      // Silent fallback - don't log in production
      if (window.cyberSellsConfig?.debug) {
        console.error('[CyberSells] Error saving player data:', error);
      }
      // Still save to memory even if localStorage fails
      _inMemoryStorage = data;
      return false;
    }
  }

  /**
   * Migrate player data between versions
   */
  function migratePlayerData(oldData) {
    const newData = getDefaultPlayerData();
    
    // Preserve core data
    if (oldData.stats) newData.stats = { ...newData.stats, ...oldData.stats };
    if (oldData.level) newData.level = oldData.level;
    if (oldData.xp) newData.xp = oldData.xp;
    if (oldData.achievements) newData.achievements = oldData.achievements;
    
    if (window.cyberSellsConfig?.debug) {
      console.log('[CyberSells] Migrated player data to version', VERSION);
    }
    return newData;
  }

  /**
   * Reset player data (for testing or account reset)
   */
  function resetPlayerData() {
    const newData = getDefaultPlayerData();
    savePlayerData(newData);
    return newData;
  }

  /**
   * Get specific player stat
   */
  function getPlayerStat(data, statName) {
    return data.stats[statName] || 0;
  }

  /**
   * Update player stats
   */
  function updatePlayerStats(data, statDeltas) {
    for (const [stat, delta] of Object.entries(statDeltas)) {
      if (data.stats.hasOwnProperty(stat)) {
        data.stats[stat] = Math.max(0, data.stats[stat] + delta);
      }
    }
    return data;
  }

  /**
   * Calculate total stat points
   */
  function getTotalStatPoints(data) {
    return Object.values(data.stats).reduce((sum, val) => sum + val, 0);
  }

  // Expose module functions
  window.CyberSells_playerData = {
    loadPlayerData,
    savePlayerData,
    resetPlayerData,
    getPlayerStat,
    updatePlayerStats,
    getTotalStatPoints,
    getDefaultPlayerData
  };

})();
